import type { EventType, ProjectState } from "@livcg/schema";
import { ArrowLeftRight, BarChart3, Keyboard, Settings, Users } from "lucide-react";
import { useRef, useState } from "react";
import { sendRaw } from "../../shared/sendRaw.js";
import { useHotkeys } from "../../shared/useHotkeys.js";
import { Button } from "../../ui/index.js";
import { allOffSurfaces } from "../allOff.js";
import { TabStats } from "../TabStats.js";
import { ClockControl } from "./ClockControl.js";
import { Drawer } from "./Drawer.js";
import { EventLog } from "./EventLog.js";
import { GridPanel } from "./GridPanel.js";
import { OverlayBoard, type OverlayBoardHandle } from "./OverlayBoard.js";
import { Panel } from "./Panel.js";
import { PenaltyPanel } from "./PenaltyPanel.js";
import { PhaseNavigator } from "./PhaseNavigator.js";
import { PlayerCompareDrawer } from "./player-compare/PlayerCompareDrawer.js";
import { QuickEvents, type QuickEventsHandle } from "./QuickEvents.js";
import { ScoreControl } from "./ScoreControl.js";
import { SetupDrawer } from "./SetupDrawer.js";
import { ShortcutsHelp } from "./ShortcutsHelp.js";
import { StatusStrip } from "./StatusStrip.js";
import { VarPanel } from "./VarPanel.js";

type DrawerKind = "lineup" | "stats" | "setup" | "players" | "shortcuts" | null;

type Props = { slug: string; state: ProjectState };

export function ControlRoom({ slug, state }: Props) {
  const overlayRef = useRef<OverlayBoardHandle>(null);
  const quickRef = useRef<QuickEventsHandle>(null);
  const [drawer, setDrawer] = useState<DrawerKind>(null);

  const fireEvent = (type: EventType) => quickRef.current?.fire(type);

  /** Kill every on-air surface at once — the current overlay mode and, if it's
   *  up, the VAR review. Mirrors SKORE's ALL OFF panic button. */
  const allOff = () => allOffSurfaces(slug, state);

  useHotkeys({
    space: () => void sendRaw("clock.toggle", { slug }),
    g: () => fireEvent("goal"),
    y: () => fireEvent("yellow_card"),
    r: () => fireEvent("red_card"),
    s: () => fireEvent("substitution"),
    // With a drawer open, Esc only closes it (the Drawer handles that).
    esc: () => {
      if (!drawer) overlayRef.current?.hideCurrent();
    },
    "shift+esc": () => allOff(),
    "shift+/": () => setDrawer("shortcuts"),
  });

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <StatusStrip slug={slug} state={state} />

      <div className="grid min-h-0 flex-1 grid-cols-[300px_1fr_340px] gap-3 p-3">
        {/* Left rail — clock, score, phase */}
        <div className="flex min-h-0 flex-col gap-3 overflow-y-auto pr-0.5">
          <ClockControl slug={slug} state={state} />
          <ScoreControl slug={slug} state={state} />
          <PhaseNavigator slug={slug} state={state} />
        </div>

        {/* Center — overlay board (top) + event log (below) */}
        <div className="flex min-h-0 flex-col gap-3 overflow-hidden">
          <div className="min-h-0 flex-[3]">
            <OverlayBoard slug={slug} state={state} controlRef={overlayRef} onAllOff={allOff} />
          </div>
          <div className="min-h-0 flex-[2]">
            <EventLog slug={slug} state={state} />
          </div>
        </div>

        {/* Right rail — quick events, VAR, secondary surfaces */}
        <div className="flex min-h-0 flex-col gap-3 overflow-y-auto pl-0.5">
          <QuickEvents slug={slug} state={state} controlRef={quickRef} />
          <VarPanel slug={slug} state={state} />
          {/* Penalties are shown only in the PEN phase; the panel also stays while
              little_pen is still on air — the operator must be able to close it. */}
          {(state.match.phase === "penalties" || state.overlay.mode === "little_pen") && (
            <PenaltyPanel slug={slug} state={state} />
          )}
          <Panel label="Boshqa">
            <div className="grid grid-cols-2 gap-1.5">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setDrawer("lineup")}
                leadingIcon={<Users className="h-3.5 w-3.5" />}
              >
                Lineup
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setDrawer("stats")}
                leadingIcon={<BarChart3 className="h-3.5 w-3.5" />}
              >
                Statistika
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setDrawer("players")}
                leadingIcon={<ArrowLeftRight className="h-3.5 w-3.5" />}
                title="Ikki o'yinchi solishtiruvi (dostats)"
              >
                O'yinchilar
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setDrawer("setup")}
                leadingIcon={<Settings className="h-3.5 w-3.5" />}
                title="Crew / stadion / homiy / ob-havo"
              >
                Sozlamalar
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setDrawer("shortcuts")}
                leadingIcon={<Keyboard className="h-3.5 w-3.5" />}
              >
                Yorliqlar
              </Button>
            </div>
          </Panel>
        </div>
      </div>

      <Drawer open={drawer === "lineup"} onClose={() => setDrawer(null)} title="Lineup · Joylashuv">
        <GridPanel slug={slug} state={state} />
      </Drawer>
      <Drawer open={drawer === "stats"} onClose={() => setDrawer(null)} title="Statistika">
        <TabStats slug={slug} state={state} />
      </Drawer>
      <Drawer
        open={drawer === "players"}
        onClose={() => setDrawer(null)}
        title="O'yinchilar"
        width="max-w-4xl"
      >
        <PlayerCompareDrawer slug={slug} state={state} />
      </Drawer>
      <Drawer open={drawer === "setup"} onClose={() => setDrawer(null)} title="Match sozlamalari">
        <SetupDrawer slug={slug} state={state} onClose={() => setDrawer(null)} />
      </Drawer>
      <Drawer
        open={drawer === "shortcuts"}
        onClose={() => setDrawer(null)}
        title="Klaviatura yorliqlari"
        width="max-w-md"
      >
        <ShortcutsHelp />
      </Drawer>
    </div>
  );
}
